import React, { useEffect, useState } from "react";
import { Plus, Trash2, Eye, EyeOff, RefreshCw, Copy, Check, Upload, Download } from "lucide-react";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const ICON_OPTIONS = [
  { url: "https://images.unsplash.com/photo-1705988142466-e468bc654eeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxjb21wYW55JTIwbG9nbyUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODI2NjMxNDR8MA&ixlib=rb-4.1.0&q=85", label: "Pills" },
  { url: "https://images.unsplash.com/photo-1633409361618-c73427e4e206?w=200&auto=format", label: "Cube" },
  { url: "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=200&auto=format", label: "Wave" },
  { url: "https://images.unsplash.com/photo-1620641788421-7a1c342ea42e?w=200&auto=format", label: "Glow" },
];

export default function AdminApps() {
  const [apps, setApps] = useState([]);
  const [reveal, setReveal] = useState({});
  const [copied, setCopied] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", redirect_uri: "", webhook_url: "", icon_url: ICON_OPTIONS[0].url, default_permissions: "" });

  const load = async () => {
    const { data } = await api.get("/admin/apps");
    setApps(data);
  };
  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    try {
      const payload = { ...form, default_permissions: form.default_permissions.split(",").map((s) => s.trim()).filter(Boolean) };
      await api.post("/admin/apps", payload);
      toast.success("App registered. Save the shared secret!");
      setOpenCreate(false);
      setForm({ name: "", description: "", redirect_uri: "", webhook_url: "", icon_url: ICON_OPTIONS[0].url, default_permissions: "" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateApp = async (id, payload) => {
    try {
      await api.patch(`/admin/apps/${id}`, payload);
      toast.success("App updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const rotateSecret = async (id) => {
    if (!confirm("Rotate shared secret? Connected apps must be updated.")) return;
    await updateApp(id, { rotate_secret: true });
  };

  const removeApp = async (id) => {
    if (!confirm("Delete this connected app? All permissions will be removed.")) return;
    try {
      await api.delete(`/admin/apps/${id}`);
      toast.success("App removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const copy = (txt, key) => {
    navigator.clipboard.writeText(txt);
    setCopied(key);
    setTimeout(() => setCopied(null), 1200);
  };

  const handleUploadIcon = (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 200 * 1024) { toast.error("Icon must be < 200KB"); return; }
    const reader = new FileReader();
    reader.onload = () => {
      if (target === "new") setForm((f) => ({ ...f, icon_url: reader.result }));
      else setEditing((ed) => ({ ...ed, icon_url: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono">// connected apps</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Connected Apps</h1>
          <p className="text-sm text-zinc-400 mt-2 max-w-xl">
            Each app has a shared secret used for HMAC verification and AES-256-GCM encryption of handoff tokens.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API_BASE}/admin/starter/download`}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs font-mono"
            data-testid="download-starter-btn"
            title="Download FastAPI + React starter template (zip) that implements the standard integration UI"
          >
            <Download className="w-3.5 h-3.5" /> integration starter.zip
          </a>
          <Button onClick={() => setOpenCreate(true)} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="create-app-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New app
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {apps.map((a) => (
          <Card key={a.id} className="bg-zinc-900/60 border-zinc-800 p-5 space-y-4" data-testid={`app-card-${a.id}`}>
            <div className="flex items-start gap-4">
              {a.icon_url && <img src={a.icon_url} alt="" className="w-12 h-12 rounded-md object-cover border border-zinc-800" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-display font-semibold text-lg truncate">{a.name}</h3>
                  {!a.is_active && <span className="text-[10px] uppercase font-mono text-red-400">disabled</span>}
                </div>
                <p className="text-xs text-zinc-500 mt-0.5 truncate">{a.description || "—"}</p>
                <div className="font-mono text-[10px] text-zinc-500 mt-2 truncate">{a.redirect_uri}</div>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">Shared secret</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 font-mono text-xs bg-zinc-950 border border-zinc-800 px-3 py-2 rounded truncate" data-testid={`app-secret-${a.id}`}>
                  {reveal[a.id] ? a.shared_secret : "•".repeat(64)}
                </code>
                <button onClick={() => setReveal({ ...reveal, [a.id]: !reveal[a.id] })} className="p-2 rounded hover:bg-zinc-800" data-testid={`reveal-secret-${a.id}`}>
                  {reveal[a.id] ? <EyeOff className="w-4 h-4 text-zinc-400" /> : <Eye className="w-4 h-4 text-zinc-400" />}
                </button>
                <button onClick={() => copy(a.shared_secret, a.id)} className="p-2 rounded hover:bg-zinc-800" data-testid={`copy-secret-${a.id}`}>
                  {copied === a.id ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4 text-zinc-400" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between text-xs">
              <div className="flex flex-wrap gap-1">
                {(a.default_permissions || []).map((p) => (
                  <span key={p} className="font-mono text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{p}</span>
                ))}
                {(a.default_permissions || []).length === 0 && <span className="text-zinc-600">no default permissions</span>}
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={a.is_active} onCheckedChange={(v) => updateApp(a.id, { is_active: v })} data-testid={`app-active-${a.id}`} />
                <span className="text-zinc-500 font-mono uppercase text-[10px]">active</span>
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
              <Button variant="ghost" size="sm" onClick={() => setEditing({ ...a, default_permissions_str: (a.default_permissions || []).join(", ") })} className="text-zinc-400 hover:text-zinc-100" data-testid={`edit-app-${a.id}`}>
                Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => rotateSecret(a.id)} className="text-amber-400 hover:text-amber-300" data-testid={`rotate-secret-${a.id}`}>
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Rotate
              </Button>
              <Button variant="ghost" size="sm" onClick={() => removeApp(a.id)} className="text-red-400 hover:text-red-300 ml-auto" data-testid={`delete-app-${a.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>
        ))}
        {apps.length === 0 && (
          <div className="lg:col-span-2 border border-dashed border-zinc-800 rounded-lg p-12 text-center">
            <div className="text-sm text-zinc-400">No connected apps yet.</div>
            <div className="text-xs text-zinc-600 mt-1">Apps registered here can authenticate users via NexusID.</div>
          </div>
        )}
      </div>

      {/* Create */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Register a connected app</DialogTitle>
            <DialogDescription className="text-zinc-400">A shared secret will be generated automatically.</DialogDescription>
          </DialogHeader>
          <AppForm form={form} setForm={setForm} onUpload={(e) => handleUploadIcon(e, "new")} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)} className="border-zinc-700">Cancel</Button>
            <Button onClick={submitCreate} className="bg-white text-zinc-950 hover:bg-zinc-200" data-testid="new-app-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display">Edit app</DialogTitle>
          </DialogHeader>
          {editing && (
            <AppForm
              form={{ ...editing, default_permissions: editing.default_permissions_str || "" }}
              setForm={(updater) => setEditing((ed) => ({ ...ed, ...(typeof updater === "function" ? updater(ed) : updater), default_permissions_str: (typeof updater === "function" ? updater(ed) : updater).default_permissions || ed.default_permissions_str }))}
              onUpload={(e) => handleUploadIcon(e, "edit")}
            />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-zinc-700">Cancel</Button>
            <Button
              onClick={() => {
                const perms = (editing.default_permissions_str || "").split(",").map((s) => s.trim()).filter(Boolean);
                updateApp(editing.id, {
                  name: editing.name, description: editing.description, icon_url: editing.icon_url,
                  redirect_uri: editing.redirect_uri, webhook_url: editing.webhook_url || "",
                  default_permissions: perms,
                });
                setEditing(null);
              }}
              className="bg-white text-zinc-950 hover:bg-zinc-200"
              data-testid="edit-app-submit"
            >Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppForm({ form, setForm, onUpload }) {
  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="app-name-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Description</Label>
        <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1" data-testid="app-desc-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Redirect URI</Label>
        <Input value={form.redirect_uri} onChange={(e) => setForm({ ...form, redirect_uri: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="https://myapp.com/auth/callback" data-testid="app-redirect-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Webhook URL <span className="text-zinc-600 normal-case">(optional)</span></Label>
        <Input value={form.webhook_url || ""} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="https://myapp.com/api/nexusid/webhook" data-testid="app-webhook-input" />
        <div className="text-[11px] text-zinc-600 mt-1">
          NexusID will POST user.disabled / user.enabled / user.deleted events here. Leave empty to disable.
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Default permissions (comma-separated)</Label>
        <Input value={form.default_permissions} onChange={(e) => setForm({ ...form, default_permissions: e.target.value })} className="bg-zinc-950 border-zinc-800 mt-1 font-mono text-xs" placeholder="read, write" data-testid="app-perms-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Icon</Label>
        <div className="flex items-center gap-2 flex-wrap">
          {ICON_OPTIONS.map((opt) => (
            <button
              key={opt.url}
              type="button"
              onClick={() => setForm({ ...form, icon_url: opt.url })}
              className={`w-12 h-12 rounded-md overflow-hidden border-2 transition-all ${form.icon_url === opt.url ? "border-zinc-100" : "border-zinc-800 hover:border-zinc-600"}`}
              data-testid={`icon-option-${opt.label.toLowerCase()}`}
            >
              <img src={opt.url} alt={opt.label} className="w-full h-full object-cover" />
            </button>
          ))}
          <label className="w-12 h-12 rounded-md border border-dashed border-zinc-700 hover:border-zinc-500 flex items-center justify-center cursor-pointer" data-testid="icon-upload-label">
            <Upload className="w-4 h-4 text-zinc-500" />
            <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
          </label>
          {form.icon_url && form.icon_url.startsWith("data:") && (
            <img src={form.icon_url} alt="uploaded" className="w-12 h-12 rounded-md object-cover border-2 border-zinc-100" />
          )}
        </div>
      </div>
    </div>
  );
}
